/**
 * useAssistants — client wrappers for the Agent Studio (hidden /assistants
 * section, per-user agentStudioEnabled flag).
 *
 *   callAgentStudio('listAssistants')            -> management gateway
 *   callAgentChat({ agentId, message, ... })     -> one full chat turn
 *   useAssistantAttachments(sessionKey)          -> composer uploads
 *                                                   (agent-uploads/{uid}/...)
 *   uploadContextFile(uid, agentId, file, cb)    -> builder knowledge uploads
 *                                                   (agent-context/{uid}/...)
 *
 * Mirrors useAgents.js (callAgents) and FutureBot's attachments hook — same
 * action-callable pattern, same upload-then-send-gs-uri model, different
 * storage prefixes so the storage.rules owner-only blocks apply.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { httpsCallable } from 'firebase/functions';
import { firebase, firebaseConfig, functionsUsEast1 } from '../utils/init-firebase';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageForChat } from '../utils/compressImage';

/** Call the agentStudio management gateway. Throws Error(message) on failure. */
export async function callAgentStudio(action, payload = {}) {
  const fn = httpsCallable(functionsUsEast1, 'agentStudio');
  try {
    const res = await fn({ action, ...payload });
    return res.data;
  } catch (err) {
    throw new Error(err?.message || 'Agent Studio call failed');
  }
}

/** Run one agentChat turn (long: the server executes the whole tool loop). */
export async function callAgentChat(payload = {}) {
  const fn = httpsCallable(functionsUsEast1, 'agentChat', { timeout: 540_000 });
  try {
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw new Error(err?.message || 'Assistant chat failed');
  }
}

// ---------------------------------------------------------------- attachments

export const MAX_ATTACHMENTS = 4;
const MAX_FILE_BYTES = 20 * 1024 * 1024; // storage rule cap

const generateId = () => crypto.randomUUID();

function safeFileName(name) {
  const cleaned = (name || 'file').replace(/[^\w.\- ]+/g, '_');
  return cleaned.length > 80 ? cleaned.slice(-80) : cleaned;
}

const formatMb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const CHAT_OK_TYPES = (type = '') =>
  type.startsWith('image/') || type === 'application/pdf' || type.startsWith('text/') || type === 'application/json';

/**
 * Pending-attachment state for the assistant chat composer. Files upload to
 * agent-uploads/{uid}/{sessionKey}/… immediately; the message then carries
 * only { storagePath, mimeType, name } (agentChat re-validates the prefix).
 */
export function useAssistantAttachments(sessionKey) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [attachments, setAttachments] = useState([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const patchAttachment = useCallback((id, updates) => {
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAttachments = useCallback(() => setAttachments([]), []);

  const uploadOne = useCallback(
    async (rawFile) => {
      let file = rawFile;
      if (!CHAT_OK_TYPES(file.type)) {
        toast({ title: 'Unsupported file', description: `${file.name}: images, PDFs, text, and CSV only.`, status: 'warning', duration: 4000 });
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast({ title: 'File too large', description: `${file.name} is ${formatMb(file.size)} — the limit is 20 MB.`, status: 'warning', duration: 4000 });
        return;
      }
      if ((file.type || '').startsWith('image/')) {
        try {
          file = await normalizeImageForChat(file);
        } catch (err) {
          toast({ title: 'Image not usable', description: err.message, status: 'warning', duration: 5000 });
          return;
        }
      }

      const id = generateId();
      const storagePath = `agent-uploads/${currentUser.uid}/${sessionKey || 'new'}/${Date.now()}_${safeFileName(file.name)}`;
      setAttachments((prev) => [
        ...prev,
        {
          id,
          name: rawFile.name || file.name,
          size: file.size,
          mimeType: file.type,
          status: 'uploading',
          progress: 0,
          storagePath,
          gsUri: `gs://${firebaseConfig.storageBucket}/${storagePath}`,
        },
      ]);

      try {
        const storageRef = firebase.storage().ref(storagePath);
        const uploadTask = storageRef.put(file);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snap) => patchAttachment(id, { progress: Math.round((snap.bytesTransferred / snap.totalBytes) * 100) }),
            reject,
            resolve
          );
        });
        patchAttachment(id, { status: 'done', progress: 100 });
      } catch (err) {
        console.error('[Assistants] Attachment upload failed:', err);
        removeAttachment(id);
        toast({ title: 'Upload failed', description: `${file.name}: ${err.message || 'unknown error'}`, status: 'error', duration: 5000 });
      }
    },
    [currentUser?.uid, sessionKey, toast, patchAttachment, removeAttachment]
  );

  const addFiles = useCallback(
    (fileList) => {
      if (!currentUser?.uid) return;
      const incoming = Array.from(fileList || []);
      if (incoming.length === 0) return;
      const room = Math.max(0, MAX_ATTACHMENTS - attachmentsRef.current.length);
      if (incoming.length > room) {
        toast({ title: 'Attachment limit', description: `Up to ${MAX_ATTACHMENTS} files per message.`, status: 'warning', duration: 4000 });
      }
      incoming.slice(0, room).forEach((file) => uploadOne(file));
    },
    [currentUser?.uid, toast, uploadOne]
  );

  const isUploading = useMemo(() => attachments.some((a) => a.status === 'uploading'), [attachments]);

  return { attachments, isUploading, addFiles, removeAttachment, clearAttachments };
}

// -------------------------------------------------------------- context files

/**
 * Upload a knowledge file for the builder to agent-context/{uid}/{agentId}/…
 * then register it (the server extracts text inline). Returns the file row.
 */
export async function uploadContextFile(uid, agentId, file, onProgress) {
  if (file.size > MAX_FILE_BYTES) throw new Error(`${file.name} is over the 20 MB limit`);
  const storagePath = `agent-context/${uid}/${agentId}/${Date.now()}_${safeFileName(file.name)}`;
  const storageRef = firebase.storage().ref(storagePath);
  const uploadTask = storageRef.put(file);
  await new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      resolve
    );
  });
  const { file: row } = await callAgentStudio('registerContextFile', {
    agentId,
    name: file.name,
    storagePath,
    mime: file.type || null,
    sizeBytes: file.size,
  });
  return row;
}

export default callAgentStudio;
